import {get} from 'lodash-es';
import {
  SCENE_TYPE_PREDICTION,
  SCENE_TYPE_SURVEY,
  STATE_CLOSED,
  STATE_DRAFT,
  STATE_OPEN
} from './constants.js';
import {
  getIfNotEmpty,
  isArrayLike,
  isEmpty,
  isNil,
  notNil
} from '@megaphonenpm/mputils/src/utils.js';

/**
 * Utilities for working with Web Poll records.
 */
export class WebPollService {
  /**
   * Returns the state for the specified web poll's pollRecord.
   *
   * @param pollRecord A complete poll record, including either .poll and .poll_result
   *                   (legacy object model) or .data and .result.
   * @returns {string}
   */
  getStateForWebPoll (pollRecord) {
    let state;

    if (get(pollRecord, `${this.getContentFieldNameForRecord(pollRecord)}.open` === true)        // Legacy web polls use .open = true;
        || get(pollRecord, `${this.getContentFieldNameForRecord(pollRecord)}.state`) === 'open'  // New web polls use .state = 'open'
        || get(pollRecord, `${this.getContentFieldNameForRecord(pollRecord)}.scenes[0].state`) === 'open') {  // Predictions set .state on the scene
      state = STATE_OPEN;
    } else if (
      get(pollRecord, `${this.getContentFieldNameForRecord(pollRecord)}.state`) === 'draft'      // Web polls set .state on the content object
      || get(pollRecord, `${this.getContentFieldNameForRecord(pollRecord)}.scenes[0].state`) === 'draft') {   // Predictions set .state on the scene
      state = STATE_DRAFT;
    } else {
      state = STATE_CLOSED;
    }

    return state;
  }

  /**
   * The getVotesByIndex() method is an adapter normalizes the new and old vote count formats.
   *
   * Given a pollRecord object, getVotesByIndex() reads the nested poll_results, where
   * numvotes are listed by either answerid (newer format) or by answer index (legacy format).
   * Based on the supplied pollRecord's nested poll_results, getVotesByIndex() then returns an
   * array of vote counts for each answer in a poll, by answer index.
   *
   * Example:
   * Given the follow poll_results object, where 123 is the number of votes for the first answer and
   * 642 is the number of votes for the second answer, getVotesByIndex() returns [123, 642].
   *
   * "poll_results": {
   *   "336e0822-5a3c-4dee-bd55-002c1dee4d7c": {
   *      "07ebf0fa-119b-4327-a70a-3750734a5343": 123,
   *      "ffe52ac2-34c9-284c-29d3-28776c8d9ab2": 642
   *    }
   * }
   *
   * @param pollRecord An object with two properties: 1) "poll": a MegaPage object
   *                   and 2) "poll_results": the current voting results for all scenes in the MegaPage.
   * @param sceneID The id of the specific scene in the MegaPage whose poll results ("votes") will be returned.
   */
  getVotesByIndex (pollRecord, sceneID = null) {
    let scenes = get(pollRecord, `${this.getContentFieldNameForRecord(pollRecord)}.scenes`);
    sceneID = isEmpty(sceneID) ? get(scenes[0], 'id') : sceneID;
    let sceneIndex = scenes.findIndex(scene => scene.id === sceneID);
    let scene = scenes[sceneIndex];
    let answers = get(scene, 'content.answers');
    let poll_results = get(pollRecord, this.getResultsFieldNameForRecord(pollRecord), {});

    if (isEmpty(answers)) {
      return [];
    } else if (isArrayLike(poll_results)) {
      // Legacy poll_results format (answerindex-based vote counts)
      let votesByIndex = [];
      answers.forEach((answer, index) => {
        let thisNumVotes = poll_results[index];
        votesByIndex.push(isNil(thisNumVotes) ? 0 : thisNumVotes);
      });
      return votesByIndex;
    } else {
      // New poll_results format (answerid-based vote counts)
      let votesByID = get(poll_results, sceneID);

      if (isNil(answers)) {
        return [];
      } else {
        let votesByIndex = [];
        answers.forEach(answer => {
          votesByIndex.push(getIfNotEmpty(votesByID, answer.id, 0));
        });
        return votesByIndex;
      }
    }
  }

  /**
   * Given an record returned by either Web Polls Service or Prediction Service, returns the
   * name of the field that contains the poll or prediction object. One of either　'poll'
   * or 'data'. This adapter will not be required when Web Poll Service refactors to use
   * 'data'.
   */
  getContentFieldName (sceneType) {
    let contentFieldName = null;

    switch (sceneType) {
      case SCENE_TYPE_SURVEY:
        contentFieldName = 'poll';
        break;

      case SCENE_TYPE_PREDICTION:
        contentFieldName = 'data';
        break;
    }

    return contentFieldName;
  }

  /**
   * Given a webpoll record or a prediction record, return the name of the field that contains
   * the record's content, which will be one of either 'poll' (legacy field name) or 'data'
   * (standard field name). This adapter is required to consume both legacy webpolls and current
   * webpolls, which have slightly different data models.
   */
  getContentFieldNameForRecord (record) {
    let contentFieldName = null;
    if (notNil(record)) {
      if (record.hasOwnProperty('poll')) {
        contentFieldName = 'poll';
      } else if (record.hasOwnProperty('data')) {
        contentFieldName = 'data';
      } else {
        console.warn('NO CONTENT FIELD ON SUPPLIED WEBPOLL/PREDICTION RECORD: ', record);
      }
    }
    return contentFieldName;
  }

  /**
   * Given a webpoll record or a prediction record, return the name of the field that contains
   * the record's content, which will be one of either 'poll' (legacy field name) or 'data'
   * (standard field name). This adapter is required to consume both legacy webpolls and current
   * webpolls, which have slightly different data models.
   */
  getResultsFieldNameForRecord (record) {
    let resultsFieldName = null;
    if (notNil(record)) {
      if (record.hasOwnProperty('poll_results')) {
        resultsFieldName = 'poll_results';
      } else if (record.hasOwnProperty('results')) {
        resultsFieldName = 'results';
      } else {
        console.warn('NO RESULTS FIELD ON SUPPLIED WEBPOLL/PREDICTION RECORD: ', record);
      }
    }
    return resultsFieldName;
  }

  /**
   * Given an record returned by either Web Polls Service or Prediction Service, returns the
   * name of the field that contains the poll or prediction results. One of either　'poll_results'
   * or 'results'. This adapter will not be required when Web Poll Service refactors to use
   * 'results'.
   */
  getResultsFieldName (sceneType) {
    let resultsFieldName = null;

    switch (sceneType) {
      case SCENE_TYPE_SURVEY:
        resultsFieldName = 'poll_results';
        break;

      case SCENE_TYPE_PREDICTION:
        resultsFieldName = 'results';
        break;
    }

    return resultsFieldName;
  }

  /**
   * Returns the human-readable name of the specified sceneType, primarily for use in logging.
   */
  getContentName (sceneType) {
    let contentName = null;
    switch (sceneType) {
      case SCENE_TYPE_SURVEY:
        contentName = 'webpoll';
        break;

      case SCENE_TYPE_PREDICTION:
        contentName = 'prediction';
        break;
    }

    return contentName;
  }
}
